import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare,
  faCheck,
  faPlus,
  faTrash,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { DeployTest, DeployTestActionItem, DeployTestItem } from '../../types';
import { API_BASE_URL } from '../../config/api';
import { DeployProgram } from '../../config/deployProgram';
import { adminUrl } from '../../config/hosts';

type LinearState = { id: string; name: string; type: string };
type LinearTeam = { id: string; name: string; states?: LinearState[] };

const defaultTodoStateId = (states: LinearState[] = []) => {
  const unstarted = states.filter((state) => state.type === 'unstarted');
  const todo = unstarted.find((state) => /to\s*-?\s*do/i.test(state.name));
  return (todo || unstarted[0] || states[0])?.id || '';
};

const PRIORITIES = [
  { value: 2, label: 'High' },
  { value: 3, label: 'Medium' },
  { value: 4, label: 'Low' },
  { value: 1, label: 'Urgent' },
];

export type LinearTicketVariant = 'action' | 'todo';

interface DeployTestActionItemsProps {
  test: DeployTest;
  program: DeployProgram;
  onActionItemsChange: (testId: string, actionItems: DeployTestActionItem[]) => void;
  createPreset?: ActionItemCreatePreset | null;
  modalOnly?: boolean;
  fixTestingOptions?: DeployTest[];
  variant?: LinearTicketVariant;
  canCreate?: boolean;
  hideHeading?: boolean;
}

const formatPreviewVersion = (value?: string) => {
  const version = String(value || '').trim().replace(/^v/i, '');
  return version ? `v${version}` : 'Not specified';
};

const testLinks = (test: DeployTest, program: DeployProgram) => ({
  publicUrl: program.publicTestHref(test),
  resultsUrl: adminUrl(program.resultsPath(test._id)),
});

const taskStatusLabel = (status?: string) => {
  if (status === 'passed') return 'Passed';
  if (status === 'flagged') return 'Flagged';
  if (status === 'failed') return 'Failed';
  if (status === 'planning') return 'Planning';
  if (status === 'aborted') return 'Aborted';
  if (status === 'testing') return 'Testing';
  return status || '';
};

export const defaultDescription = (test: DeployTest, program: DeployProgram) => {
  const links = testLinks(test, program);
  const lines = [
    `Created from ${program.track === 'internal' ? 'internal' : 'beta'} test: ${test.name}`,
    '',
    `Firmware: ${formatPreviewVersion(test.firmwareVersion)}`,
    `Mobile App (iOS): ${formatPreviewVersion(test.mobileAppVersionIos || test.mobileAppVersion)}`,
    `Mobile App (Android): ${formatPreviewVersion(test.mobileAppVersionAndroid || test.mobileAppVersion)}`,
    '',
    `Public test page: ${links.publicUrl}`,
    `Results page: ${links.resultsUrl}`,
  ];
  if (test.overview?.trim()) {
    lines.push('', test.overview.trim());
  }
  return lines.join('\n');
};

export const descriptionFromTask = (
  test: DeployTest,
  program: DeployProgram,
  task: Pick<DeployTestItem, 'text' | 'description'> & {
    status?: string;
    tester?: string;
    phone?: string;
  }
) => {
  const lines = [
    `Task: ${task.text?.trim() || 'Untitled task'}`,
  ];
  const status = taskStatusLabel(task.status);
  if (status) lines.push(`Status: ${status}`);
  if (task.tester) lines.push(`Tester: ${task.tester}`);
  if (task.phone) lines.push(`Phone: ${task.phone}`);
  if (task.description?.trim()) {
    lines.push('', task.description.trim());
  }
  lines.push('', defaultDescription(test, program));
  return lines.join('\n');
};

export interface ActionItemCreatePreset {
  title?: string;
  description?: string;
  nonce: number;
}

const formatTicketAnswer = (value: string, type?: string) => {
  if (!value) return '—';
  if (type === 'boolean') {
    if (value === 'yes') return 'Yes';
    if (value === 'no') return 'No';
  }
  return value;
};

export const descriptionFromQuestion = (
  test: DeployTest,
  program: DeployProgram,
  question: {
    prompt: string;
    type?: string;
    answers: Array<{ tester: string; phone: string; value: string; additionalInfo?: string }>;
  }
) => {
  const lines = [`Question: ${question.prompt.trim() || 'Untitled question'}`, '', 'Answers:'];
  if (!question.answers.length) {
    lines.push('No answers yet');
  } else {
    question.answers.forEach((answer) => {
      lines.push(`- ${answer.tester} · ${answer.phone}: ${formatTicketAnswer(answer.value, question.type)}`);
      if (answer.additionalInfo?.trim()) {
        lines.push(`  ${answer.additionalInfo.trim()}`);
      }
    });
  }
  lines.push('', defaultDescription(test, program));
  return lines.join('\n');
};

export const descriptionFromAnswer = (
  test: DeployTest,
  program: DeployProgram,
  question: { prompt: string; type?: string },
  answer: { tester: string; phone: string; value: string; additionalInfo?: string }
) => {
  const lines = [
    `Question: ${question.prompt.trim() || 'Untitled question'}`,
    `Answer from ${answer.tester} · ${answer.phone}:`,
    formatTicketAnswer(answer.value, question.type),
  ];
  if (answer.additionalInfo?.trim()) {
    lines.push('', answer.additionalInfo.trim());
  }
  lines.push('', defaultDescription(test, program));
  return lines.join('\n');
};

const priorityLabel = (item: DeployTestActionItem) => (
  item.priorityName === 'Normal' ? 'Medium' : item.priorityName || 'No priority'
);

const linearWorkspaceBase = (url?: string) => {
  const match = String(url || '').match(/^(https:\/\/linear\.app\/[^/]+)/i);
  return match?.[1] || '';
};

const assigneeProfileSlug = (name?: string) => (
  String(name || '')
    .trim()
    .split(/\s+/)[0]
    ?.toLowerCase()
    .replace(/[^a-z0-9-]/g, '') || ''
);

const assigneeHref = (item: DeployTestActionItem) => {
  if (item.assigneeUrl) return item.assigneeUrl;
  if (!item.assigneeName) return '';
  const workspaceBase = linearWorkspaceBase(item.url);
  if (!workspaceBase) return '';
  const slug = assigneeProfileSlug(item.assigneeName);
  return slug ? `${workspaceBase}/profiles/${slug}` : '';
};

const LinearPriorityIcon = ({ priority = 0 }: { priority?: number }) => {
  if (priority === 0) {
    return <span className="linear-priority-icon is-none" aria-hidden="true" />;
  }
  if (priority === 1) {
    return (
      <span className="linear-priority-icon is-urgent" aria-hidden="true">
        !
      </span>
    );
  }
  if (![2, 3, 4].includes(priority)) return null;
  return (
    <span className={`linear-priority-icon is-bars is-p${priority}`} aria-hidden="true">
      <span />
      <span />
      <span />
    </span>
  );
};

const VARIANT_COPY: Record<LinearTicketVariant, {
  title: string;
  empty: string;
  add: string;
  boxClass: string;
  addButtonClass: string;
  path: string;
  responseKey: 'actionItems' | 'todoItems';
  field: 'actionItems' | 'todoItems';
  noun: string;
}> = {
  action: {
    title: 'Action Items',
    empty: 'No action items yet',
    add: 'Add Action Item',
    boxClass: 'deploy-test-card-action-items',
    addButtonClass: 'btn-outline-danger',
    path: 'action-items',
    responseKey: 'actionItems',
    field: 'actionItems',
    noun: 'action item',
  },
  todo: {
    title: 'TODOs',
    empty: 'No TODOs yet',
    add: 'Add TODO Item',
    boxClass: 'deploy-test-card-todo-items',
    addButtonClass: 'btn-outline-success',
    path: 'todo-items',
    responseKey: 'todoItems',
    field: 'todoItems',
    noun: 'TODO',
  },
};

const DeployTestActionItems: React.FC<DeployTestActionItemsProps> = ({
  test,
  program,
  onActionItemsChange,
  createPreset = null,
  modalOnly = false,
  fixTestingOptions = [],
  variant = 'action',
  canCreate = true,
  hideHeading = false,
}) => {
  const copy = VARIANT_COPY[variant];
  const actionItems = test[copy.field] || [];
  const showFixTarget = variant === 'action';
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [teamId, setTeamId] = useState('');
  const [stateId, setStateId] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [priority, setPriority] = useState(3);
  const [teams, setTeams] = useState<LinearTeam[]>([]);
  const [users, setUsers] = useState<Array<{ id: string; name: string }>>([]);
  const teamStates = teams.find((team) => team.id === teamId)?.states || [];
  const [configured, setConfigured] = useState(true);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [savingFixTestingId, setSavingFixTestingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const eligibleFixTestingOptions = fixTestingOptions.filter((option) => (
    option._id !== test._id && ['planning', 'testing'].includes(option.status)
  ));

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingOptions(true);
    setError(null);
    axios.get(`${API_BASE_URL}${program.testsApi}/action-item-options`, { withCredentials: true })
      .then((response) => {
        if (cancelled) return;
        const nextTeams: LinearTeam[] = response.data.teams || [];
        const nextUsers = response.data.users || [];
        const nextTeamId = teamId || nextTeams[0]?.id || '';
        setConfigured(response.data.configured !== false);
        setTeams(nextTeams);
        setUsers(nextUsers);
        setTeamId(nextTeamId);
        setStateId((current) => {
          const states = nextTeams.find((team) => team.id === nextTeamId)?.states || [];
          return current && states.some((state) => state.id === current)
            ? current
            : defaultTodoStateId(states);
        });
      })
      .catch((err) => {
        if (!cancelled) {
          setConfigured(false);
          setError(err.response?.data?.message || 'Could not load Linear teams');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingOptions(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, program.testsApi]);

  const openCreate = (preset?: { title?: string; description?: string }) => {
    setTitle(preset?.title || '');
    setDescription(preset?.description || defaultDescription(test, program));
    setAssigneeId('');
    setStateId('');
    setPriority(3);
    setError(null);
    setOpen(true);
  };

  useEffect(() => {
    if (!createPreset) return;
    openCreate({
      title: createPreset.title,
      description: createPreset.description,
    });
  }, [createPreset?.nonce]);

  const createItem = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!title.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      const response = await axios.post(
        `${API_BASE_URL}${program.testsApi}/${test._id}/${copy.path}`,
        { title, description, teamId, assigneeId, stateId, priority },
        { withCredentials: true }
      );
      onActionItemsChange(test._id, response.data[copy.responseKey] || []);
      setOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not create Linear ticket');
    } finally {
      setSaving(false);
    }
  };

  const removeItem = async (itemId?: string) => {
    if (!itemId || removingId) return;
    setRemovingId(itemId);
    try {
      const response = await axios.delete(
        `${API_BASE_URL}${program.testsApi}/${test._id}/${copy.path}/${itemId}`,
        { withCredentials: true }
      );
      onActionItemsChange(test._id, response.data[copy.responseKey] || []);
    } catch (_err) {
      // Keep the current list if unlink fails.
    } finally {
      setRemovingId(null);
    }
  };

  const updateFixTestingTarget = async (itemId: string | undefined, fixTestingInTestId: string) => {
    if (!itemId || savingFixTestingId) return;
    setSavingFixTestingId(itemId);
    setUpdateError(null);
    try {
      const response = await axios.patch(
        `${API_BASE_URL}${program.testsApi}/${test._id}/${copy.path}/${itemId}`,
        { fixTestingInTestId: fixTestingInTestId || null },
        { withCredentials: true }
      );
      onActionItemsChange(test._id, response.data[copy.responseKey] || []);
    } catch (err: any) {
      setUpdateError(err.response?.data?.message || `Could not update ${copy.noun}`);
    } finally {
      setSavingFixTestingId(null);
    }
  };

  return (
    <>
      {!modalOnly && (
      <div className={`deploy-test-card-tasks-box ${copy.boxClass}`}>
        {!hideHeading && (
          <div className="deploy-test-card-meta">
            <span>
              <strong>{copy.title}</strong>
            </span>
          </div>
        )}
        <div className={`deploy-test-action-table${hideHeading ? ' is-flush' : ''}${showFixTarget ? '' : ' no-fix-target'}`}>
            <div className="deploy-test-action-row is-head">
              <span />
              <span>Ticket</span>
              <span>Status</span>
              <span>Priority</span>
              <span>Assignee</span>
              {showFixTarget && <span>Fix Testing In</span>}
              <span />
            </div>
            <ul className="deploy-test-action-list">
              {actionItems.length === 0 && (
                <li className="deploy-test-action-row is-empty">
                  <span />
                  <span className="deploy-test-action-empty">{copy.empty}</span>
                  <span />
                  <span />
                  <span />
                  {showFixTarget && <span />}
                  <span />
                </li>
              )}
              {actionItems.map((item) => {
                const includesSelectedTarget = eligibleFixTestingOptions.some((option) => (
                  option._id === item.fixTestingInTestId
                ));
                const selectedFixTarget = fixTestingOptions.find((option) => (
                  option._id === item.fixTestingInTestId
                )) || (item.fixTestingInTestId && item.fixTestingInTestName
                  ? { name: item.fixTestingInTestName, slug: undefined }
                  : null);
                const assigneeLink = assigneeHref(item);
                return (
                <li key={item._id || item.issueId} className="deploy-test-action-row">
                  <span
                    className={`deploy-test-action-mark is-${item.stateType || 'backlog'}`}
                    aria-hidden="true"
                  >
                    {item.stateType === 'completed' ? (
                      <FontAwesomeIcon icon={faCheck} />
                    ) : item.stateType === 'canceled' ? (
                      <FontAwesomeIcon icon={faXmark} />
                    ) : null}
                  </span>
                  <a
                    href={item.url || undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="deploy-test-action-link"
                  >
                    <span className="deploy-test-action-id">{item.identifier || 'Ticket'}</span>
                    <span className="deploy-test-action-title">{item.title || 'Untitled'}</span>
                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                  </a>
                  <span className={`deploy-test-action-state is-${item.stateType || 'backlog'}`}>
                    {item.stateName || 'Unknown'}
                  </span>
                  <span
                    className={`deploy-test-action-priority is-p${item.priority ?? 0}`}
                    title={priorityLabel(item)}
                    aria-label={priorityLabel(item)}
                  >
                    <LinearPriorityIcon priority={item.priority} />
                    {priorityLabel(item)}
                  </span>
                  {assigneeLink ? (
                    <a
                      href={assigneeLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="deploy-test-action-assignee deploy-test-action-assignee-link"
                    >
                      {item.assigneeName || 'Unassigned'}
                    </a>
                  ) : (
                    <span className="deploy-test-action-assignee">
                      {item.assigneeName || 'Unassigned'}
                    </span>
                  )}
                  {showFixTarget && (
                    <div className="deploy-test-action-fix-target">
                      <select
                        className="form-select form-select-sm deploy-test-action-fix-select"
                        value={item.fixTestingInTestId || ''}
                        onChange={(event) => updateFixTestingTarget(item._id, event.target.value)}
                        disabled={!item._id || savingFixTestingId === item._id}
                        aria-label={`Fix testing target for ${item.identifier || 'ticket'}`}
                      >
                        <option value="">Not Specified</option>
                        {item.fixTestingInTestId && item.fixTestingInTestName && !includesSelectedTarget && (
                          <option value={item.fixTestingInTestId}>{item.fixTestingInTestName}</option>
                        )}
                        {eligibleFixTestingOptions.map((option) => (
                          <option key={option._id} value={option._id}>
                            {option.name}
                          </option>
                        ))}
                      </select>
                      {selectedFixTarget && (
                        <a
                          href={program.publicTestHref(selectedFixTarget)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-outline-secondary btn-sm deploy-test-action-fix-link"
                          aria-label={`Open ${selectedFixTarget.name} testing page`}
                        >
                          <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                        </a>
                      )}
                    </div>
                  )}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => removeItem(item._id)}
                    disabled={removingId === item._id}
                    aria-label={`Detach ${item.identifier || 'ticket'}`}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </li>
                );
              })}
            </ul>
          </div>
        {updateError && <div className="alert alert-danger py-2 mt-2 mb-0">{updateError}</div>}
        <button
          type="button"
          className={`btn ${copy.addButtonClass} btn-sm mt-2`}
          onClick={() => openCreate()}
          disabled={!canCreate}
          title={
            canCreate
              ? undefined
              : variant === 'todo'
                ? 'TODOs can only be added while this test is in Planning'
                : 'Action items can be added after this test leaves Planning'
          }
        >
          <FontAwesomeIcon icon={faPlus} className="me-1" />
          {copy.add}
        </button>
      </div>
      )}

      {open && (
        <div className="beta-leave-modal" role="dialog" aria-modal="true" aria-labelledby="action-item-title">
          <form className="beta-leave-modal-card action-item-modal-card" onSubmit={createItem}>
            <h2 id="action-item-title">Create Linear ticket</h2>
            <p className="text-muted">
              This opens a ticket in Linear and attaches it to <strong>{test.name}</strong>.
            </p>
            {!configured && (
              <div className="alert alert-warning">
                Connect Linear on the APIs page before creating {copy.noun}s.
              </div>
            )}
            <label className="form-label fw-bold" htmlFor={`action-item-name-${test._id}`}>
              Title
            </label>
            <input
              id={`action-item-name-${test._id}`}
              className="form-control"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Fix Wi-Fi commissioning on iOS"
              disabled={saving}
              required
            />
            <label className="form-label fw-bold mt-3" htmlFor={`action-item-team-${test._id}`}>
              Team
            </label>
            <select
              id={`action-item-team-${test._id}`}
              className="form-select"
              value={teamId}
              onChange={(event) => {
                const nextTeamId = event.target.value;
                const states = teams.find((team) => team.id === nextTeamId)?.states || [];
                setTeamId(nextTeamId);
                setStateId(defaultTodoStateId(states));
              }}
              disabled={saving || loadingOptions || !teams.length}
              required
            >
              {!teams.length && <option value="">{loadingOptions ? 'Loading teams…' : 'No teams found'}</option>}
              {teams.map((team) => (
                <option key={team.id} value={team.id}>{team.name}</option>
              ))}
            </select>
            <label className="form-label fw-bold mt-3" htmlFor={`action-item-state-${test._id}`}>
              Status
            </label>
            <select
              id={`action-item-state-${test._id}`}
              className="form-select"
              value={stateId}
              onChange={(event) => setStateId(event.target.value)}
              disabled={saving || loadingOptions || !teamStates.length}
            >
              {!teamStates.length && (
                <option value="">{loadingOptions ? 'Loading statuses…' : 'Todo'}</option>
              )}
              {teamStates.map((state) => (
                <option key={state.id} value={state.id}>{state.name}</option>
              ))}
            </select>
            <label className="form-label fw-bold mt-3" htmlFor={`action-item-assignee-${test._id}`}>
              Assignee
            </label>
            <select
              id={`action-item-assignee-${test._id}`}
              className="form-select"
              value={assigneeId}
              onChange={(event) => setAssigneeId(event.target.value)}
              disabled={saving || loadingOptions}
            >
              <option value="">{loadingOptions ? 'Loading people…' : 'Unassigned'}</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>{user.name}</option>
              ))}
            </select>
            <label className="form-label fw-bold mt-3" htmlFor={`action-item-priority-${test._id}`}>
              Priority
            </label>
            <select
              id={`action-item-priority-${test._id}`}
              className="form-select"
              value={priority}
              onChange={(event) => setPriority(Number(event.target.value))}
              disabled={saving}
            >
              {PRIORITIES.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <label className="form-label fw-bold mt-3" htmlFor={`action-item-body-${test._id}`}>
              Description
            </label>
            <textarea
              id={`action-item-body-${test._id}`}
              className="form-control"
              rows={8}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              disabled={saving}
            />
            {error && <div className="alert alert-danger py-2 mt-3 mb-0">{error}</div>}
            <div className="beta-leave-modal-actions">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setOpen(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving || !configured || !title.trim() || !teamId}
              >
                {saving ? 'Creating…' : 'Create Ticket'}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default DeployTestActionItems;
